import { useMutation } from "@tanstack/react-query";
import apiService from "../../api/axiosInstance";
import { useDispatch } from "react-redux";
import { setIsNewUser, setUser } from "./user.reducer";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getProfileCompletionState } from "./profileCompletion";
import {
  startJourney,
  setDatadogUserContext,
  logLoginSubmitted,
  logLoginApiResult,
  logUserContextSet,
  logPostLoginRoutingDecision,
} from "../../utils/datadogLogger";

const useLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (loginData) => apiService.post("api/login", loginData),
    onSuccess: (data) => {
      console.log("Login successful:", data);
      const profileData = data.data.data;

      // Log login API success with response body
      logLoginApiResult(
        profileData?.email || 'unknown@example.com',
        data?.status || 200,
        'login',
        data.data, // Full response body
        null
      );

      dispatch(
        setUser({ user: profileData, token: profileData.jwtoken })
      );

      // Set Datadog user context
      setDatadogUserContext(profileData);

      // Log user context set
      logUserContextSet(
        profileData.id || profileData.user_id,
        profileData.email,
        profileData.plan_name,
        profileData.is_paid,
        profileData.year_id
      );

      const { isNameMissing, isDobMissing, isAvatarMissing } =
        getProfileCompletionState(profileData);

      let destination = "/yearsdashboard";
      let reason = "complete_profile";

      if (isNameMissing || isDobMissing || isAvatarMissing) {
        dispatch(setIsNewUser(true));
        destination = "/afterlogin";
        reason = "incomplete_profile";
        navigate("/afterlogin", {
          state: {
            loginData: profileData,
            showNameTab: isNameMissing,
            showDobTab: isDobMissing,
            showAvatarTab: isAvatarMissing,
          },
          replace: true,
        });
      } else {
        navigate("/yearsdashboard", { replace: true });
      }

      // Log post-login routing decision
      logPostLoginRoutingDecision(
        profileData.id || profileData.user_id,
        profileData.email,
        destination,
        reason
      );
    },
    onError: (error) => {
      // Log login API error with response body
      logLoginApiResult(
        'unknown@example.com',
        error?.response?.status || 500,
        'login',
        error?.response?.data || {},
        error.response?.data?.message || "Login failed. Please try again."
      );

      toast.error(
        error.response?.data?.message || "Login failed. Please try again.",
        {
          ariaLabel: "Login Error",
          autoClose: 2000,
          theme: "colored",
          hideProgressBar: true,
          className: "font-mali",
        }
      );
    },
  });
};

export const useUpdateUser = () => {
  const dispatch = useDispatch();
  return useMutation({
    mutationKey: ["updateUser"],
    mutationFn: (updateData) => {
      var form_data = new FormData();

      for (var key in updateData) {
        form_data.append(key, updateData[key]);
      }
      return apiService.post("api/updatesignup", form_data);
    },
    onSuccess: (data) => {
      console.log("User updated successfully:", data);
      dispatch(
        setUser({ user: data.data.data, token: data.data.data.jwtoken })
      );
    },
    onError: (error) => {
      console.error(
        "User update failed:",
        error.response?.data?.message || error.message
      );
    },
  });
};

export default useLogin;
